import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase, supabaseUrl, supabaseAnonKey } from '../../supabase/supabase';
import './OTPVerification.css';
import { motion } from 'framer-motion';

const OTPVerification = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { email, phoneNumber, method } = location.state || {}; // method: 'email', 'sms', 'whatsapp'

    // 4-digit OTP as per Edge Function spec
    const [otp, setOtp] = useState(['', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        if (!email && !phoneNumber) {
            navigate('/');
        }
    }, [email, phoneNumber, navigate]);

    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return;

        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        // Focus next input
        if (element.value && index < 3) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    const verifyOtp = async () => {
        const code = otp.join('');
        if (code.length !== 4) {
            setError('Please enter the 4-digit code.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Verify OTP via Edge Function
            const payload = email ? { email, otpCode: code } : { phoneNumber, otpCode: code };

            console.log('Verifying OTP with payload:', payload);

            const response = await fetch(`${supabaseUrl}/functions/v1/phone-login-otp-verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'apikey': supabaseAnonKey,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            console.log('Verify response:', response.status, data);

            if (!response.ok || !data?.success) {
                throw new Error(data?.error || 'Invalid OTP or Verification Failed');
            }

            if (!data?.token) {
                throw new Error('No session token returned.');
            }

            // Complete Authentication with Supabase using the token from Edge Function
            const { data: authData, error: authError } = await supabase.auth.verifyOtp({
                token_hash: data.token,
                type: 'magiclink'
            });

            if (authError) throw authError;

            // User Flow Check (Existing vs New User)
            if (authData?.user) {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('id')
                    .eq('user_id', authData.user.id)
                    .maybeSingle();

                if (!profile) {
                    navigate('/setup-account');
                } else {
                    navigate('/feed');
                }
            } else {
                navigate('/');
            }

        } catch (err) {
            console.error('OTP Verification Error:', err);
            setError(err.message || 'Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;

        setLoading(true);
        setError('');

        try {
            let endpoint = '';
            let payload = {};

            if (email) {
                endpoint = 'email-login-otp-send';
                payload = { email };
            } else {
                endpoint = 'phone-login-otp-send';
                payload = { phoneNumber, method: method || 'whatsapp' };
            }

            const response = await fetch(`${supabaseUrl}/functions/v1/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'apikey': supabaseAnonKey,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.error || 'Failed to resend');

            setResendTimer(30);
            setShowToast(true);

            // Auto-hide toast after 3 seconds
            setTimeout(() => {
                setShowToast(false);
            }, 3000);

        } catch (err) {
            console.error('Resend Error:', err);
            setError('Failed to resend code.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="otp-container">
            <motion.div
                className="otp-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h2>Verify it's you</h2>
                <p className="otp-subtitle">
                    We sent a 4-digit code to {email || phoneNumber}.<br />
                    Enter it below to sign in.
                </p>

                <div className="otp-inputs">
                    {otp.map((data, index) => (
                        <input
                            key={index}
                            id={`otp-input-${index}`}
                            type="text"
                            maxLength="1"
                            value={data}
                            onChange={(e) => handleChange(e.target, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onFocus={(e) => e.target.select()}
                        />
                    ))}
                </div>

                {error && <p className="otp-error">{error}</p>}

                <button
                    className="otp-verify-btn"
                    onClick={verifyOtp}
                    disabled={loading || otp.join('').length !== 4}
                >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>

                <p className="resend-text">
                    Didn't receive code?{' '}
                    <span
                        className={`resend-link ${resendTimer > 0 ? 'disabled' : ''}`}
                        onClick={handleResend}
                    >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                    </span>
                </p>

                <button className="back-btn" onClick={() => navigate('/')}>
                    Back to Login
                </button>
            </motion.div>

            {/* Modern Toast Notification for Resend Success */}
            {showToast && (
                <motion.div
                    className="otp-resend-toast-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="otp-resend-toast-content"
                        initial={{ scale: 0.8, y: -20 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                        <div className="otp-resend-toast-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="otp-resend-toast-text">
                            <h4>Code Sent Successfully!</h4>
                            <p>Check your {email ? 'email' : 'WhatsApp'} for the new code</p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default OTPVerification;
